import { useState, useEffect, useCallback } from "react";
import { copyToClipboard, showToast } from "../../../libraries/clipboardUtils";
import { buildAppActivityFields, withAppActivityFields } from "../../../libraries/activityLogger";
import { deleteAnydeskURL, getAnydeskByBranchURL, saveAnydeskURL, updateAnydeskURL } from "../../../libraries/apiUrl";
import {
  formatAnydeskCode,
  getAnydeskFromResponse,
  getAnydeskIdFromRecord,
  getAnydeskPasswordFromRecord
} from "../../../libraries/anydeskUtils";
import { _delete, _get, _post, _put } from "../../../libraries/fetch";
import Modal from "../../../components/Modal";

const normalizeRecordId = (recordId, visibleAnydeskId = "") => {
  const normalizedRecordId = String(recordId || "").trim();
  const normalizedVisibleId = String(visibleAnydeskId || "").trim();

  if (!normalizedRecordId || normalizedRecordId === normalizedVisibleId) {
    return "";
  }

  return normalizedRecordId;
};

const getAnydeskRecordId = (anydesk) => {
  if (!anydesk) return "";
  const visibleAnydeskId = getAnydeskIdFromRecord(anydesk);
  return normalizeRecordId(
    anydesk._id || anydesk.recordId || anydesk.anydeskRecordId || "",
    visibleAnydeskId
  );
};

const getNormalizedId = (value) => {
  if (!value) return "";

  if (typeof value === "object") {
    return String(value._id || value.id || value.branchId || "").trim();
  }

  return String(value).trim();
};

const getNumericAnydeskCode = formatAnydeskCode;

function AnyDesk({ branchId, companyId, branchName }) {
  const [anydeskId, setAnydeskId] = useState("");
  const [anydeskPassword, setAnydeskPassword] = useState("");
  const [copiedAnydeskId, setCopiedAnydeskId] = useState(false);
  const [copiedAnydeskPassword, setCopiedAnydeskPassword] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modalId, setModalId] = useState("");
  const [modalPassword, setModalPassword] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [anydeskRecordId, setAnydeskRecordId] = useState("");
  const [hasSavedAnydesk, setHasSavedAnydesk] = useState(false);

  const buildAnydeskPayload = useCallback((id, password, recordId = "") => {
    const numericCode = getNumericAnydeskCode(id);
    const payload = {
      company: companyId,
      branch: branchId,
      companyId,
      branchId,
      anydeskCode: numericCode ? Number(numericCode) : numericCode,
      anydeskPassword: (password || "").trim()
    };

    if (recordId) {
      payload._id = recordId;
    }

    return payload;
  }, [branchId, companyId]);

  const clearLocalAnydeskCredentials = () => {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("anydesk_")) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Error clearing local AnyDesk credentials:", error);
    }
  };

  // Load AnyDesk credentials on component mount
  useEffect(() => {
    clearLocalAnydeskCredentials();

    const resetAnydeskData = () => {
      setAnydeskRecordId("");
      setHasSavedAnydesk(false);
      setAnydeskId("");
      setAnydeskPassword("");
    };

    const applyAnydeskData = (anydesk) => {
      if (!anydesk || typeof anydesk !== "object" || Array.isArray(anydesk)) {
        resetAnydeskData();
        return;
      }

      const apiRecordId = getAnydeskRecordId(anydesk);
      const apiId = getAnydeskIdFromRecord(anydesk);
      const apiPassword = getAnydeskPasswordFromRecord(anydesk);
      setAnydeskRecordId(apiRecordId);
      setHasSavedAnydesk(Boolean(apiRecordId || apiId || apiPassword));
      setAnydeskId(getNumericAnydeskCode(apiId));
      setAnydeskPassword(apiPassword);
    };

    const loadAnydeskData = async () => {
      const currentBranchId = getNormalizedId(branchId);
      if (!currentBranchId) {
        resetAnydeskData();
        return;
      }

      try {
        const data = await _get(getAnydeskByBranchURL(currentBranchId), { useCache: false });

        if (!data || data?.success === false) {
          throw new Error(data?.message || "Failed to load AnyDesk credentials.");
        }

        const branchAnydesk = getAnydeskFromResponse(data);

        if (branchAnydesk) {
          applyAnydeskData(branchAnydesk);
        } else {
          resetAnydeskData();
        }
      } catch (error) {
        console.error("Error loading AnyDesk data:", error);
        resetAnydeskData();
      }
    };

    if (branchId) {
      loadAnydeskData();
    }
  }, [branchId]);

  const handleOpenModal = () => {
    if (hasSavedAnydesk) {
      setModalId(getNumericAnydeskCode(anydeskId));
      setModalPassword(anydeskPassword);
    } else {
      setModalId("");
      setModalPassword("");
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalId("");
    setModalPassword("");
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleSaveAnydesk = async () => {
    const numericModalId = getNumericAnydeskCode(modalId);

    if (!numericModalId || !modalPassword.trim()) {
      showToast("Please fill in both ID and Password fields", "error");
      return;
    }

    try {
      setIsSaving(true);
      const trimmedId = numericModalId;
      const trimmedPassword = modalPassword.trim();
      const isUpdate = Boolean(anydeskRecordId);
      const basePayload = buildAnydeskPayload(trimmedId, trimmedPassword, anydeskRecordId);
      const payload = withAppActivityFields(basePayload, {
        activityType: isUpdate ? "Update AnyDesk" : "Add AnyDesk",
        initiatedFrom: "AnyDesk",
        name: branchName || branchId,
        previousValue: isUpdate
          ? { anydeskCode: anydeskId, anydeskPassword: anydeskPassword }
          : null,
        updatedValue: { anydeskCode: trimmedId, anydeskPassword: trimmedPassword }
      });
      const currentBranchId = getNormalizedId(branchId);
      const response = isUpdate
        ? await _put(updateAnydeskURL(currentBranchId), payload)
        : await _post(saveAnydeskURL(currentBranchId), payload);

      if (response?.success === false) {
        throw new Error(response.message || "Unable to save AnyDesk credentials");
      }

      const savedAnydesk = getAnydeskFromResponse(response);
      const savedRecordId = getAnydeskRecordId(savedAnydesk) || anydeskRecordId;

      setAnydeskRecordId(savedRecordId);
      setHasSavedAnydesk(true);

      setAnydeskId(trimmedId);
      setAnydeskPassword(trimmedPassword);

      showToast("AnyDesk credentials saved successfully", "success");

      handleCloseModal();
    } catch (error) {
      console.error("Error saving AnyDesk credentials:", error);
      showToast(`Error: ${error.message || 'Unknown error occurred'}`, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAnydesk = async () => {
    if (!anydeskRecordId) {
      showToast("No saved AnyDesk credentials found to delete", "error");
      return;
    }

    try {
      setIsDeleting(true);
      const response = await _delete(
        deleteAnydeskURL(getNormalizedId(branchId)),
        buildAppActivityFields({
          activityType: "Delete AnyDesk",
          initiatedFrom: "AnyDesk",
          name: branchName || branchId,
          previousValue: { anydeskCode: anydeskId, anydeskPassword: anydeskPassword }
        })
      );

      if (response?.success === false) {
        throw new Error(response.message || "Unable to delete AnyDesk credentials");
      }

      setAnydeskRecordId("");
      setHasSavedAnydesk(false);
      setAnydeskId("");
      setAnydeskPassword("");
      showToast("AnyDesk credentials deleted successfully", "success");
      handleCloseDeleteModal();
    } catch (error) {
      console.error("Error deleting AnyDesk credentials:", error);
      showToast(`Error: ${error.message || 'Unknown error occurred'}`, "error");
    } finally {
      setIsDeleting(false);
    }
  };

  async function handleCopyAnydeskId(e) {
    if (e) {
      e.stopPropagation();
    }
    const result = await copyToClipboard(anydeskId || "N/A", { label: "AnyDesk ID" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedAnydeskId(true);
      setTimeout(() => {
        setCopiedAnydeskId(false);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  async function handleCopyAnydeskPassword(e) {
    if (e) {
      e.stopPropagation();
    }
    const result = await copyToClipboard(anydeskPassword || "N/A", { label: "AnyDesk password" });
    if (result.success) {
      showToast(result.message, 'success');
      setCopiedAnydeskPassword(true);
      setTimeout(() => {
        setCopiedAnydeskPassword(false);
      }, 2000);
    } else {
      showToast(result.message, 'error');
    }
  }

  const anydeskActionLabel = hasSavedAnydesk ? "Update AnyDesk" : "Add AnyDesk";
  const anydeskModalHeading = hasSavedAnydesk ? "Update AnyDesk Credentials" : "Add AnyDesk Credentials";

  return (
    <>
    <div className="configMain">
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '30px',
        justifyContent: 'flex-start'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="#e84118">
            <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3v9zm6.5 0h-3v-9h3v9zm6 0h-3v-9h3v9zM19 9H5V5h14v4z"/>
          </svg>
          <h1 style={{ margin: 0 }}>AnyDesk Info</h1>
        </div>
      </div>

      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '20px'
      }}>
        <div
          style={{
            backgroundColor: '#2a2d35',
            border: '1px solid #444',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            cursor: 'default',
            transition: 'transform 0.2s, box-shadow 0.2s',
            position: 'relative'
          }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(232, 65, 24, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.3)';
                  }}
        >
          <div style={{
            backgroundColor: '#1e2127',
            borderRadius: '8px',
            padding: '20px'
          }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '14px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                  AnyDesk ID
                </span>
                <button
                  onClick={(e) => handleCopyAnydeskId(e)}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #e84118',
                    cursor: 'pointer',
                    padding: '8px 16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    transition: 'all 0.2s',
                    fontSize: '13px',
                    color: '#e84118',
                    fontWeight: '600'
                  }}
                  title="Copy AnyDesk ID"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e84118';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e84118';
                  }}
                >
                  {copiedAnydeskId ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor">
                        <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor">
                        <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={anydeskId}
                readOnly
                placeholder="Enter AnyDesk ID"
                style={{
                  width: '100%',
                  backgroundColor: '#2a2d35',
                  padding: '14px 16px',
                  borderRadius: '6px',
                  border: '1px solid #444',
                  fontSize: '16px',
                  color: '#fff',
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  outline: 'none',
                  cursor: 'default',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#e84118';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#444';
                }}
              />
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <span style={{ fontSize: '14px', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                  AnyDesk Password
                </span>
                <button
                  onClick={(e) => handleCopyAnydeskPassword(e)}
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid #e84118',
                    cursor: 'pointer',
                    padding: '8px 16px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    transition: 'all 0.2s',
                    fontSize: '13px',
                    color: '#e84118',
                    fontWeight: '600'
                  }}
                  title="Copy AnyDesk Password"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#e84118';
                    e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e84118';
                  }}
                >
                  {copiedAnydeskPassword ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor">
                        <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"/>
                      </svg>
                      Copied!
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor">
                        <path d="M384 336l-192 0c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l140.1 0L400 115.9 400 320c0 8.8-7.2 16-16 16zM192 384l192 0c35.3 0 64-28.7 64-64l0-204.1c0-12.7-5.1-24.9-14.1-33.9L366.1 14.1c-9-9-21.2-14.1-33.9-14.1L192 0c-35.3 0-64 28.7-64 64l0 256c0 35.3 28.7 64 64 64zM64 128c-35.3 0-64 28.7-64 64L0 448c0 35.3 28.7 64 64 64l192 0c35.3 0 64-28.7 64-64l0-32-48 0 0 32c0 8.8-7.2 16-16 16L64 464c-8.8 0-16-7.2-16-16l0-256c0-8.8 7.2-16 16-16l32 0 0-48-32 0z" />
                      </svg>
                      Copy
                    </>
                  )}
                </button>
              </div>
              <input
                type="text"
                value={anydeskPassword}
                readOnly
                placeholder="Enter AnyDesk Password"
                style={{
                  width: '100%',
                  backgroundColor: '#2a2d35',
                  padding: '14px 16px',
                  borderRadius: '6px',
                  border: '1px solid #444',
                  fontSize: '16px',
                  color: '#fff',
                  fontFamily: 'monospace',
                  fontWeight: '600',
                  letterSpacing: '1px',
                  outline: 'none',
                  cursor: 'default',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#e84118';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#444';
                }}
              />
            </div>
          </div>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '12px',
          marginTop: '20px',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={handleOpenModal}
            style={{
              backgroundColor: '#e84118',
              border: 'none',
              color: '#fff',
              padding: '12px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d63910';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#e84118';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="16" height="16" fill="currentColor">
              <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"/>
            </svg>
            {anydeskActionLabel}
          </button>
          {hasSavedAnydesk && (
            <button
              onClick={() => setIsDeleteModalOpen(true)}
              style={{
                backgroundColor: 'transparent',
                border: '1px solid #d51717',
                color: '#d51717',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#d51717';
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#d51717';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Delete AnyDesk
            </button>
          )}
        </div>
      </div>
    </div>

    <Modal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      title={
        <>
          <span style={{
            display: 'block',
            color: '#fff',
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '12px'
          }}>
            Are you sure you want to do this?
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 16px',
            borderRadius: '999px',
            backgroundColor: '#1e2127',
            border: '1px solid #444',
            color: '#e84118',
            fontSize: '14px',
            fontWeight: '700',
            marginBottom: '14px'
          }}>
            {branchName || 'Branch Name N/A'}
          </span>
          <span style={{ display: 'block' }}>
            {anydeskModalHeading}
          </span>
        </>
      }
      maxWidth="500px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            color: '#888',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            AnyDesk ID
          </label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={modalId}
            onChange={(e) => setModalId(getNumericAnydeskCode(e.target.value))}
            placeholder="Enter AnyDesk ID"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              backgroundColor: '#1e2127',
              padding: '12px 16px',
              borderRadius: '6px',
              border: '1px solid #444',
              fontSize: '16px',
              color: '#fff',
              fontFamily: 'monospace',
              fontWeight: '500',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#e84118';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#444';
            }}
          />
        </div>

        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            color: '#888',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>
            AnyDesk Password
          </label>
          <input
            type="password"
            value={modalPassword}
            onChange={(e) => setModalPassword(e.target.value)}
            placeholder="Enter AnyDesk Password"
            style={{
              width: '100%',
              boxSizing: 'border-box',
              backgroundColor: '#1e2127',
              padding: '12px 16px',
              borderRadius: '6px',
              border: '1px solid #444',
              fontSize: '16px',
              color: '#fff',
              fontFamily: 'monospace',
              fontWeight: '500',
              outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = '#e84118';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = '#444';
            }}
          />
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end',
          marginTop: '10px'
        }}>
          <button
            onClick={handleCloseModal}
            disabled={isSaving}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #888',
              color: '#888',
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s',
              opacity: isSaving ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.borderColor = '#fff';
                e.currentTarget.style.color = '#fff';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.borderColor = '#888';
                e.currentTarget.style.color = '#888';
              }
            }}
          >
            No
          </button>
          <button
            onClick={handleSaveAnydesk}
            disabled={isSaving}
            style={{
              backgroundColor: '#e84118',
              border: 'none',
              color: '#fff',
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              transition: 'all 0.2s',
              opacity: isSaving ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!isSaving) {
                e.currentTarget.style.backgroundColor = '#d63910';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSaving) {
                e.currentTarget.style.backgroundColor = '#e84118';
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Yes'}
          </button>
        </div>
      </div>
    </Modal>

    <Modal
      isOpen={isDeleteModalOpen}
      onClose={handleCloseDeleteModal}
      title={
        <>
          <span style={{
            display: 'block',
            color: '#fff',
            fontSize: '18px',
            fontWeight: '700',
            marginBottom: '12px'
          }}>
            Are you sure you want to delete this?
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '8px 16px',
            borderRadius: '999px',
            backgroundColor: '#1e2127',
            border: '1px solid #444',
            color: '#e84118',
            fontSize: '14px',
            fontWeight: '700'
          }}>
            {branchName || 'Branch Name N/A'}
          </span>
        </>
      }
      maxWidth="420px"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <p style={{ color: '#ddd', margin: 0 }}>
          This will remove the saved AnyDesk credentials for this client.
        </p>
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <button
            onClick={handleCloseDeleteModal}
            disabled={isDeleting}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #888',
              color: '#888',
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: isDeleting ? 0.6 : 1
            }}
          >
            No
          </button>
          <button
            onClick={handleDeleteAnydesk}
            disabled={isDeleting}
            style={{
              backgroundColor: '#d51717',
              border: 'none',
              color: '#fff',
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: isDeleting ? 0.6 : 1
            }}
          >
            {isDeleting ? 'Deleting...' : 'Yes'}
          </button>
        </div>
      </div>
    </Modal>
    </>
  );
}

export default AnyDesk;